import { Card, ProgressBar, Table } from "react-bootstrap";
import type {
  ACOvershootData,
  OvershootDemandEntry,
  OvershootRedistributionData,
} from "../lib/explainTypes";
import { formatCurrency, formatPercent } from "../../../lib/numbers";
import RedistributionWarningCard from "./RedistributionWarningCard";
import {
  CHART_COLORS,
  ChartLegend,
  DivergingBarChart,
  HBarChart,
  buildColorScale,
  type DivergingRow,
} from "./soft/charts";

interface Props {
  data: OvershootRedistributionData | null;
  resolveAccountName?: (accountId: number) => string;
  // Shared asset-class color scale from the soft-completion view; a
  // local scale over the overshoot classes is built when absent.
  acColor?: (ac: string) => string;
}

const num = (s: string | undefined): number =>
  s == null || s === "" ? 0 : Number(s);

// The E4 demand entry nests per-account dollars under `by_account`,
// alongside `total` / `budget`.
function demandAccounts(entry: OvershootDemandEntry): [string, number][] {
  return Object.entries(entry.by_account ?? {}).map(
    ([k, v]) => [k, num(v)] as [string, number],
  );
}

// Per-AC stacked bar: the absorbed share in green, the residual share
// (still over target) in red. Widths are proportions of `detected`.
function AbsorbedResidualBar({ row }: { row: ACOvershootData }) {
  const detected = num(row.detected);
  if (detected <= 0) return <span className="text-muted">—</span>;
  // Cap at 100% total: floating-point rounding in the emitter can make
  // `absorbed + residual` drift slightly past `detected`, which would
  // otherwise overflow the stacked bar.
  const absorbedPct = Math.min((num(row.absorbed) / detected) * 100, 100);
  const residualPct = Math.min(
    (num(row.residual) / detected) * 100,
    100 - absorbedPct,
  );
  return (
    <ProgressBar>
      <ProgressBar variant="success" now={absorbedPct} />
      <ProgressBar variant="danger" now={residualPct} />
    </ProgressBar>
  );
}

// Static copy: the three redistribution tiers (contract s5_overshoot
// tier_mechanism) — freed weight goes asset class → risk → account
// model, never to cash.
const TIERS = [
  {
    tier: "TIER 1 · asset class",
    title: "Level the under-target classes",
    detail:
      "Freed weight goes first into classes the household is still under budget on — if the account's model can reach them.",
  },
  {
    tier: "TIER 2 · risk",
    title: "Steer toward the risk the household needs",
    detail:
      "No reachable class room → fill the under-target risk buckets through whatever sleeve the model has there.",
  },
  {
    tier: "TIER 3 · account model",
    title: "Settle pro-rata on the model",
    detail:
      "Remainder spreads across the account's own model. Pinned positions stay put; the unabsorbable rest becomes the residual warning.",
  },
];

function TierStrip() {
  return (
    <div className="d-flex flex-column flex-md-row gap-2 mb-3">
      {TIERS.map((t, i) => (
        <div
          key={t.tier}
          className="border rounded bg-body-tertiary px-3 py-2 flex-fill"
          style={{ flexBasis: 0 }}
        >
          <div
            className="font-monospace fw-bold"
            style={{ fontSize: "0.7rem" }}
          >
            {i > 0 && <span className="text-muted me-1">→</span>}
            {t.tier}
          </div>
          <div className="fw-bold" style={{ fontSize: "0.83rem" }}>
            {t.title}
          </div>
          <div className="text-muted" style={{ fontSize: "0.73rem" }}>
            {t.detail}
          </div>
        </div>
      ))}
    </div>
  );
}

// E4-gated: post-fit demand vs budget, who was flexible vs pinned, the
// trim rows, and the per-account redeploy. Renders nothing on
// pre-enrichment payloads (fields absent).
function RedistributionDetail({
  data,
  resolveAccountName,
  acColor,
}: {
  data: OvershootRedistributionData;
  resolveAccountName: (accountId: number) => string;
  acColor: (ac: string) => string;
}) {
  const demand = data.demand_by_ac;
  const pools = data.pools;
  const trimRows = data.trim_rows;
  const redeploy = data.redeploy;
  if (
    demand == null &&
    pools == null &&
    (trimRows == null || trimRows.length === 0) &&
    (redeploy == null || redeploy.length === 0)
  ) {
    return null;
  }

  const demandEntries = Object.entries(demand ?? {});
  const demandAccountIds = [
    ...new Set(
      demandEntries.flatMap(([, e]) => demandAccounts(e).map(([id]) => id)),
    ),
  ].sort();

  // Redeploy chart: scaled-down classes leftward (from trim_rows),
  // redeploy targets rightward (tier-labeled).
  const redeployRows: DivergingRow[] = (redeploy ?? []).map((entry) => ({
    label: resolveAccountName(entry.account_id),
    sub: `freed ${formatPercent(num(entry.freed_pct), 1)}`,
    neg: (trimRows ?? [])
      .filter((t) => t.account_id === entry.account_id)
      .map((t) => ({
        value: num(t.trimmed),
        color: acColor(t.ac),
        title: `${t.ac} −${formatCurrency(num(t.trimmed))}`,
      })),
    pos: (entry.into ?? []).map((into) => ({
      value: num(into.dollars),
      color: acColor(into.target),
      title: `${into.target} +${formatCurrency(num(into.dollars))} (Tier ${into.tier})`,
    })),
  }));
  const redeployLegendKeys = [
    ...new Set([
      ...(trimRows ?? []).map((t) => t.ac),
      ...(redeploy ?? []).flatMap((e) => (e.into ?? []).map((i) => i.target)),
    ]),
  ];

  return (
    <>
      {demandEntries.length > 0 && (
        <>
          <HBarChart
            title="Post-fit demand vs budget (tick = budget; red = overshoot)"
            rows={demandEntries.map(([ac, entry]) => {
              const total = num(entry.total);
              const budget = num(entry.budget);
              const over = Math.max(total - budget, 0);
              return {
                label: ac,
                segments: [
                  { value: Math.min(total, budget), color: acColor(ac) },
                  {
                    value: over,
                    color: CHART_COLORS.over,
                    title: `over ${formatCurrency(over)}`,
                  },
                ],
                ticks: [{ value: budget, label: "budget" }],
                note:
                  over > 0 ? `over ${formatCurrency(over)}` : "within budget",
              };
            })}
          />
          <Table size="sm" responsive className="mt-2 mb-0">
            <caption className="text-muted small">
              Demand per class — per-account columns tie the overshoot to who
              overspent it
            </caption>
            <thead>
              <tr>
                <th>Asset class</th>
                {demandAccountIds.map((id) => (
                  <th key={id} className="text-end">
                    {resolveAccountName(Number(id))}
                  </th>
                ))}
                <th className="text-end">Demand</th>
                <th className="text-end">Budget</th>
                <th className="text-end">Over</th>
              </tr>
            </thead>
            <tbody>
              {demandEntries.map(([ac, entry]) => {
                const perAccount = new Map(demandAccounts(entry));
                const total = num(entry.total);
                const budget = num(entry.budget);
                return (
                  <tr key={ac}>
                    <td>{ac}</td>
                    {demandAccountIds.map((id) => (
                      <td key={id} className="text-end">
                        {perAccount.has(id)
                          ? formatCurrency(perAccount.get(id) ?? 0)
                          : "—"}
                      </td>
                    ))}
                    <td className="text-end">{formatCurrency(total)}</td>
                    <td className="text-end">{formatCurrency(budget)}</td>
                    <td className="text-end text-danger">
                      {formatCurrency(Math.max(total - budget, 0))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </>
      )}

      {pools != null && Object.keys(pools).length > 0 && (
        <Table size="sm" responsive className="mt-3 mb-0">
          <caption className="text-muted small">
            Flexible vs pinned — pinned positions carry the constraint that
            holds them in place
          </caption>
          <thead>
            <tr>
              <th>Asset class</th>
              <th>Flexible (can trim)</th>
              <th>Pinned (untouched)</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(pools).map(([ac, pool]) => (
              <tr key={ac}>
                <td>{ac}</td>
                <td>
                  {(pool.flexible ?? []).length > 0
                    ? (pool.flexible ?? [])
                        .map(
                          (p) =>
                            `${resolveAccountName(p.account_id)} ${formatCurrency(num(p.dollars))}`,
                        )
                        .join(" · ")
                    : "—"}
                </td>
                <td>
                  {(pool.pinned ?? []).length > 0
                    ? (pool.pinned ?? [])
                        .map(
                          (p) =>
                            `${resolveAccountName(p.account_id)} ${formatCurrency(num(p.dollars))}${
                              (p.flags ?? []).length > 0
                                ? ` (${(p.flags ?? []).join(", ")})`
                                : ""
                            }`,
                        )
                        .join(" · ")
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {trimRows != null && trimRows.length > 0 && (
        <Table size="sm" responsive className="mt-3 mb-0">
          <caption className="text-muted small">
            The trim — flexible positions scaled down per class
          </caption>
          <thead>
            <tr>
              <th>Account</th>
              <th>Asset class</th>
              <th className="text-end">Before</th>
              <th className="text-end">After</th>
              <th className="text-end">Trimmed</th>
              <th className="text-end">×</th>
            </tr>
          </thead>
          <tbody>
            {trimRows.map((t, i) => (
              <tr key={`${t.account_id}-${t.ac}-${i}`}>
                <td>{resolveAccountName(t.account_id)}</td>
                <td>{t.ac}</td>
                <td className="text-end">{formatCurrency(num(t.before))}</td>
                <td className="text-end">{formatCurrency(num(t.after))}</td>
                <td className="text-end">{formatCurrency(num(t.trimmed))}</td>
                <td className="text-end">
                  {(Number(num(t.factor).toFixed(3)) + 0).toFixed(3)}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {redeployRows.length > 0 && (
        <>
          <DivergingBarChart
            title="Net per-account change: scaled down (left) → redeployed into (right)"
            rows={redeployRows}
          />
          <ChartLegend
            items={redeployLegendKeys.map((key) => ({
              label: key,
              color: acColor(key),
            }))}
          />
        </>
      )}
    </>
  );
}

// Renders the soft-completion overshoot redistribution pass: per asset
// class, how much household overshoot was detected after the tax-aware
// pass, how much was absorbed by scaling flexible positions down, and
// how much residual remains. Renders nothing when the pass detected no
// overshoot (`data` is null).
export default function OvershootRedistributionPanel({
  data,
  resolveAccountName,
  acColor,
}: Props) {
  if (!data || data.overshoot_by_ac.length === 0) return null;
  const resolveName =
    resolveAccountName ?? ((accountId: number) => `Account ${accountId}`);
  const color =
    acColor ?? buildColorScale(data.overshoot_by_ac.map((r) => r.ac).sort());
  return (
    <Card className="mb-3">
      <Card.Header>Overshoot Redistribution</Card.Header>
      <Card.Body>
        <TierStrip />
        <Table size="sm" responsive className="mb-0">
          <caption className="text-muted small">
            Per class: detected = absorbed + residual
          </caption>
          <thead>
            <tr>
              <th>Asset class</th>
              <th className="text-end">Detected</th>
              <th className="text-end">Absorbed</th>
              <th className="text-end">Residual</th>
              <th style={{ minWidth: "8rem" }}>Absorbed / residual</th>
            </tr>
          </thead>
          <tbody>
            {data.overshoot_by_ac.map((row) => {
              const residual = num(row.residual);
              return (
                <tr key={row.ac}>
                  <td>{row.ac}</td>
                  <td className="text-end">
                    {formatCurrency(num(row.detected))}
                  </td>
                  <td className="text-end text-success">
                    {formatCurrency(num(row.absorbed))}
                  </td>
                  <td
                    className={`text-end ${residual > 0 ? "text-danger" : ""}`}
                  >
                    {formatCurrency(residual)}
                  </td>
                  <td>
                    <AbsorbedResidualBar row={row} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>

        {data.warnings.length > 0 && (
          <div className="mt-3">
            {data.warnings.map((w) => (
              <RedistributionWarningCard
                key={w.ac}
                warning={w}
                resolveAccountName={resolveAccountName}
              />
            ))}
          </div>
        )}

        <RedistributionDetail
          data={data}
          resolveAccountName={resolveName}
          acColor={color}
        />
      </Card.Body>
    </Card>
  );
}
